import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Box,
  Typography,
  Alert
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  AdminPanelSettings as AdminIcon,
  Person as PersonIcon,
  Visibility as ViewerIcon,
  ContentCopy as CopyIcon,
  Check as CheckIcon,
  Close as CloseIcon
} from '@mui/icons-material';

interface User {
  id: string;
  username: string;
  name: string;
  email: string;
  role: 'admin' | 'member' | 'viewer';
  color?: string;
  createdAt: string;
  lastLogin?: string;
}

interface UserManagementProps {
  open: boolean;
  onClose: () => void;
  token: string;
  apiBaseUrl: string;
}

const ROLES = {
  ADMIN: 'admin',
  MEMBER: 'member',
  VIEWER: 'viewer'
};

const ROLE_LABELS = {
  admin: 'Administrator',
  member: 'Mitglied',
  viewer: 'Betrachter'
};

const ROLE_COLORS = {
  admin: '#f57c00',
  member: '#1976d2',
  viewer: '#757575'
};

const ROLE_ICONS = {
  admin: AdminIcon,
  member: PersonIcon,
  viewer: ViewerIcon
};

export default function UserManagement({ open, onClose, token, apiBaseUrl }: UserManagementProps) {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [editForm, setEditForm] = useState({
    name: '',
    email: '',
    role: '',
    color: ''
  });
  
  // New user creation state
  const [creatingUser, setCreatingUser] = useState(false);
  const [newUserForm, setNewUserForm] = useState({
    username: '',
    name: '',
    email: '',
    password: '',
    role: 'viewer',
    color: ''
  });
  
  // Password change state
  const [changingPassword, setChangingPassword] = useState<string | null>(null);
  const [passwordForm, setPasswordForm] = useState({
    newPassword: '',
    confirmPassword: ''
  });



  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    

    
    try {
      const response = await fetch(`${apiBaseUrl}/users`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('🔧 Users API error:', errorText);
        throw new Error('Failed to fetch users');
      }

      const userList = await response.json();
      setUsers(userList);
    } catch (err) {
      console.error('🔧 Error fetching users:', err);
      setError(err instanceof Error ? err.message : 'Benutzer konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  const updateUser = async (userId: string, updates: Partial<User>) => {
    try {
      const response = await fetch(`${apiBaseUrl}/users/${userId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updates)
      });

      if (!response.ok) {
        throw new Error('Failed to update user');
      }

      // Refresh users list
      await fetchUsers();
      setEditingUser(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Benutzer konnte nicht aktualisiert werden');
    }
  };

  const deleteUser = async (userId: string) => {
    if (!window.confirm('Sind Sie sicher, dass Sie diesen Benutzer löschen möchten?')) {
      return;
    }

    try {
      const response = await fetch(`${apiBaseUrl}/users/${userId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete user');
      }

      // Refresh users list
      await fetchUsers();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Benutzer konnte nicht gelöscht werden');
    }
  };

  const createUser = async () => {
    setError(null);
    
    if (!newUserForm.username || !newUserForm.name || !newUserForm.email || !newUserForm.password) {
      setError('Alle Felder sind erforderlich');
      return;
    }

    if (newUserForm.password.length < 6) {
      setError('Passwort muss mindestens 6 Zeichen lang sein');
      return;
    }

    try {
      const response = await fetch(`${apiBaseUrl}/users`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newUserForm)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create user');
      }

      // Refresh users list
      await fetchUsers();
      setCreatingUser(false);
      setNewUserForm({
        username: '',
        name: '',
        email: '',
        password: '',
        role: 'viewer',
        color: ''
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Benutzer konnte nicht erstellt werden');
    }
  };

  const changePassword = async (userId: string) => {
    setError(null);
    
    if (!passwordForm.newPassword || !passwordForm.confirmPassword) {
      setError('Alle Felder sind erforderlich');
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      setError('Passwort muss mindestens 6 Zeichen lang sein');
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setError('Passwörter stimmen nicht überein');
      return;
    }

    try {
      const response = await fetch(`${apiBaseUrl}/users/${userId}/password`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ password: passwordForm.newPassword })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to change password');
      }

      setChangingPassword(null);
      setPasswordForm({ newPassword: '', confirmPassword: '' });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Passwort konnte nicht geändert werden');
    }
  };

  const handleEdit = (user: User) => {
    setEditingUser(user);
    setEditForm({
      name: user.name,
      email: user.email,
      role: user.role,
      color: user.color || ''
    });
  };

  const handleSaveEdit = () => {
    if (!editingUser) return;
    
    updateUser(editingUser.id, {
      name: editForm.name,
      email: editForm.email,
      role: editForm.role as 'admin' | 'member' | 'viewer',
      color: editForm.color
    });
  };

  const handleCancelEdit = () => {
    setEditingUser(null);
    setEditForm({ name: '', email: '', role: '', color: '' });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const generateUserUrl = (userId: string) => {
    return `${window.location.origin}/user/${userId}`;
  };

  const copyUserUrl = async (userId: string) => {
    const url = generateUserUrl(userId);
    try {
      await navigator.clipboard.writeText(url);
      // You could add a notification here if you pass a notification function as prop
      console.log('URL copied:', url);
    } catch (error) {
      console.error('Failed to copy URL:', error);
    }
  };

  useEffect(() => {
    if (open) {
      fetchUsers();
    }
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        Benutzerverwaltung
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {/* Add User Section */}
        <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Neuen Benutzer hinzufügen</Typography>
            <Button
              variant="contained"
              onClick={() => setCreatingUser(!creatingUser)}
              size="small"
            >
              {creatingUser ? 'Abbrechen' : 'Benutzer hinzufügen'}
            </Button>
          </Box>
          
          {creatingUser && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="Benutzername"
                  value={newUserForm.username}
                  onChange={(e) => setNewUserForm(prev => ({ ...prev, username: e.target.value }))}
                  size="small"
                  fullWidth
                />
                <TextField
                  label="Name"
                  value={newUserForm.name}
                  onChange={(e) => setNewUserForm(prev => ({ ...prev, name: e.target.value }))}
                  size="small"
                  fullWidth
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="E-Mail"
                  type="email"
                  value={newUserForm.email}
                  onChange={(e) => setNewUserForm(prev => ({ ...prev, email: e.target.value }))}
                  size="small"
                  fullWidth
                />
                <FormControl size="small" fullWidth>
                  <InputLabel>Rolle</InputLabel>
                  <Select
                    value={newUserForm.role}
                    label="Rolle"
                    onChange={(e) => setNewUserForm(prev => ({ ...prev, role: e.target.value }))}
                  >
                    {Object.entries(ROLES).map(([key, value]) => (
                      <MenuItem key={key} value={value}>
                        {ROLE_LABELS[value as keyof typeof ROLE_LABELS]}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="Passwort"
                  type="password"
                  value={newUserForm.password}
                  onChange={(e) => setNewUserForm(prev => ({ ...prev, password: e.target.value }))}
                  size="small"
                  fullWidth
                />
                <TextField
                  label="Farbe (optional)"
                  type="color"
                  value={newUserForm.color}
                  onChange={(e) => setNewUserForm(prev => ({ ...prev, color: e.target.value }))}
                  size="small"
                  sx={{ width: 120 }}
                  InputLabelProps={{ shrink: true }}
                />
                <Button
                  variant="contained"
                  onClick={createUser}
                  disabled={!newUserForm.username || !newUserForm.name || !newUserForm.email || !newUserForm.password}
                  sx={{ minWidth: 120 }}
                >
                  Erstellen
                </Button>
              </Box>
            </Box>
          )}
        </Box>
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <Typography>Lade Benutzer...</Typography>
          </Box>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Benutzername</TableCell>
                  <TableCell>E-Mail</TableCell>
                  <TableCell>Rolle</TableCell>
                  <TableCell>Farbe</TableCell>
                  <TableCell>URL</TableCell>
                  <TableCell>Erstellt</TableCell>
                  <TableCell>Letzter Login</TableCell>
                  <TableCell>Aktionen</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => {
                  const RoleIcon = ROLE_ICONS[user.role];
                  return (
                    <TableRow key={user.id}>
                      <TableCell>
                        {editingUser?.id === user.id ? (
                          <TextField
                            size="small"
                            value={editForm.name}
                            onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                          />
                        ) : (
                          user.name
                        )}
                      </TableCell>
                      <TableCell>{user.username}</TableCell>
                      <TableCell>
                        {editingUser?.id === user.id ? (
                          <TextField
                            size="small"
                            value={editForm.email}
                            onChange={(e) => setEditForm(prev => ({ ...prev, email: e.target.value }))}
                          />
                        ) : (
                          user.email
                        )}
                      </TableCell>
                      <TableCell>
                        {editingUser?.id === user.id ? (
                          <FormControl size="small">
                            <Select
                              value={editForm.role}
                              onChange={(e) => setEditForm(prev => ({ ...prev, role: e.target.value }))}
                            >
                              {Object.entries(ROLES).map(([key, value]) => (
                                <MenuItem key={key} value={value}>
                                  {ROLE_LABELS[value as keyof typeof ROLE_LABELS]}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        ) : (
                          <Chip
                            label={ROLE_LABELS[user.role]}
                            icon={<RoleIcon />}
                            size="small"
                            sx={{
                              bgcolor: ROLE_COLORS[user.role],
                              color: 'white',
                              fontWeight: 'bold'
                            }}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        {editingUser?.id === user.id ? (
                          <TextField
                            type="color"
                            size="small"
                            value={editForm.color}
                            onChange={(e) => setEditForm(prev => ({ ...prev, color: e.target.value }))}
                            sx={{ width: 80 }}
                          />
                        ) : (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box
                              sx={{
                                width: 20,
                                height: 20,
                                borderRadius: '50%',
                                bgcolor: user.color || '#1976d2',
                                border: '1px solid #ccc'
                              }}
                            />
                            <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                              {user.color || 'Standard'}
                            </Typography>
                          </Box>
                        )}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                            {generateUserUrl(user.id)}
                          </Typography>
                          <Tooltip title="URL kopieren">
                            <IconButton
                              size="small"
                              onClick={() => copyUserUrl(user.id)}
                              color="primary"
                            >
                              <CopyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                      <TableCell>{formatDate(user.createdAt)}</TableCell>
                      <TableCell>
                        {user.lastLogin ? formatDate(user.lastLogin) : 'Nie'}
                      </TableCell>
                      <TableCell>
                        {editingUser?.id === user.id ? (
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Tooltip title="Speichern">
                              <IconButton
                                size="small"
                                onClick={handleSaveEdit}
                                color="success"
                              >
                                <CheckIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Abbrechen">
                              <IconButton
                                size="small"
                                onClick={handleCancelEdit}
                                color="error"
                              >
                                <CloseIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        ) : (
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Tooltip title="Bearbeiten">
                              <IconButton
                                size="small"
                                onClick={() => handleEdit(user)}
                                color="primary"
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Passwort ändern">
                              <IconButton
                                size="small"
                                onClick={() => setChangingPassword(user.id)}
                                color="secondary"
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Löschen">
                              <IconButton
                                size="small"
                                onClick={() => deleteUser(user.id)}
                                color="error"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>
          Schließen
        </Button>
      </DialogActions>

      {/* Password Change Dialog */}
      <Dialog 
        open={changingPassword !== null} 
        onClose={() => setChangingPassword(null)}
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>
          Passwort ändern für {users.find(u => u.id === changingPassword)?.name || 'Benutzer'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Neues Passwort"
              type="password"
              value={passwordForm.newPassword}
              onChange={(e) => setPasswordForm(prev => ({ ...prev, newPassword: e.target.value }))}
            />
            <TextField
              fullWidth
              label="Passwort bestätigen"
              type="password"
              value={passwordForm.confirmPassword}
              onChange={(e) => setPasswordForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setChangingPassword(null)}>
            Abbrechen
          </Button>
          <Button
            onClick={() => changingPassword && changePassword(changingPassword)}
            variant="contained"
            disabled={!passwordForm.newPassword || !passwordForm.confirmPassword}
          >
            Passwort ändern
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
} 